import * as s from "@gromia/components/theme/t.module.css";
import { Dropdown } from "@gromia/components/ui/Dropdown";
import { parseDataUriToText, siteUrl } from "@gromia/utils";
import { useString } from "@gromia/utils/useString";
import cx from "classnames";
import * as A from "fp-ts/lib/Array";
import { pipe } from "fp-ts/lib/function";
import * as O from "fp-ts/lib/Option";
import React from "react";

export type ShareData = {
  text: string;
  textl?: string; // text long
  path: string;
  media: O.Option<string>;
  siteUrl?: string;
  color?: string;
};

export function ShareLinks(props: {
  data: ShareData;
  small?: boolean;
  aClassName?: string;
  aStyle?: React.CSSProperties;
  iconClassName?: string;
  filter?: string[];
  label: (x: ReturnType<typeof linksForShareData>[number]) => React.ReactNode;
}) {
  const list = linksForShareData(props.data).filter(x =>
    props.filter ? props.filter.includes(x.key) : true
  );

  return (
    <div>
      {list.map(x => (
        <a
          key={x.name}
          href={x.href}
          onClick={pipe(x.popupHeight, O.map(sharePopup), O.toUndefined)}
          target="_blank"
          style={props.aStyle}
          className={cx(s.pr4)}
        >
          {props.label(x)}
        </a>
      ))}
    </div>
  );
}

export function ShareLinkIcons(props: {
  data: ShareData;
  small?: boolean;
  aClassName?: string;
  aStyle?: React.CSSProperties;
  iconClassName?: string;
  children?: React.ReactNode;
}) {
  const iconc = cx(
    props.small ? s.mx3 : cx(s.mxLg4, s.mx3),
    s.cursorPointer,
    props.iconClassName,
  );

  const list = linksForShareData(props.data);

  return (
    <div className={cx(s.dFlex, s.alignItemsCenter, s.justifyContentCenter)}>
      {props.children}
      {list.map(x => (
        <a
          key={x.name}
          href={x.href}
          onClick={pipe(x.popupHeight, O.map(sharePopup), O.toUndefined)}
          target="_blank"
          style={props.aStyle}
          className={props.aClassName}
        >
          <span
            className={iconc}
            style={props.small ? { maxHeight: 16 } : {}}
            dangerouslySetInnerHTML={{ __html: parseDataUriToText(x.icon) }}
          />
        </a>
      ))}
    </div>
  );
}

export function ShareDropdown(props: {
  data: ShareData;
  children: React.ReactElement<any>;
}) {
  const list = linksForShareData(props.data);

  return (
    <Dropdown
      label={""}
      trigger={props.children}
      contentNode={() => (
        <ul className={cx(s.listUnstyled, s.p3, s.m0)}>
          {list.map(x => (
            <li key={x.name}>
              <a
                className={cx(s.dBlock, s.fontSizeLarge1, s.fontWeightBold)}
                href={x.href}
                onClick={pipe(x.popupHeight, O.map(sharePopup), O.toUndefined)}
                target="_blank"
              >
                {x.name}
              </a>
            </li>
          ))}
        </ul>
      )}
    />
  );
}

export function ShareButtonActions(props: {
  data: ShareData;
  prefix: string;
  txKey: string;
  tx: Record<string, string | null>;
}) {
  const { tx } = useString(props.txKey, props.tx);
  const list = linksForShareData(props.data);

  const x = list.map((x, idx) => (
    <div key={x.key} className={cx(idx > 0 ? s.pt4 : null)}>
      <div className={cx(s.row, s.textLeft, s.bgLightBlue2, s.rounded)}>
        <div className={cx(s.col, s.col12, s.colMd7, s.p4)}>
          <div className={cx(s.fontWeightBold, s.fontSizeBig)}>
            {tx(props.prefix + "Title-" + x.key)}
          </div>
          <div className={cx(s.fontSizeSmall1)}>
            {tx(props.prefix + "Text-" + x.key)}
          </div>
        </div>
        <div
          className={cx(
            s.col,
            s.col12,
            s.colMd5,
            s.dFlex,
            s.justifyContentStart,
            s.alignItemsCenter,
            s.pt0,
            s.pMd4,
            s.pr4,
            s.pl4,
            s.pb4,
          )}
        >
          <a
            key={x.name}
            href={x.href}
            onClick={pipe(x.popupHeight, O.map(sharePopup), O.toUndefined)}
            target="_blank"
            className={cx(s.btn, s.btnPrimary, s.wMd100)}
          >
            {tx(props.prefix + "Cta-" + x.key)}
          </a>
        </div>
      </div>
    </div>
  ));

  return <React.Fragment>{x}</React.Fragment>;
}

function linksForShareData(data: ShareData): Array<{
  key: string;
  name: string;
  href: string;
  icon: string;
  popupHeight: O.Option<number>;
}> {
  const text = encodeURIComponent(data.text);
  const urlunenc = (data.siteUrl || siteUrl) + data.path;
  const media = data.media;
  const url = encodeURIComponent(urlunenc);
  const textPlusUrl = encodeURIComponent(data.text + " " + urlunenc);
  const textl = data.textl
    ? encodeURIComponent(data.textl.replace(/\r?\n/g, "\r\n"))
    : null;

  return A.compact([
    O.some({
      key: "email",
      name: "E-mail",
      href: `mailto:?subject=${text}&body=${textl ? textl : url}`,
      icon: require("@gromia/components/svg/paper-plane.svg").default,
      popupHeight: O.none,
    }),
    O.some({
      key: "whatsapp",
      name: "WhatsApp",
      href: `https://wa.me/?text=${textl ? textl : textPlusUrl}`,
      icon: require("@gromia/components/svg/whatsapp.svg").default,
      popupHeight: O.some(500),
    }),
    O.some({
      key: "facebook",
      name: "Facebook",
      href: `https://www.facebook.com/sharer.php?u=${url}`,
      icon: require("@gromia/components/svg/facebook.svg").default,
      popupHeight: O.some(800),
    }),
    O.some({
      key: "linkedin",
      name: "Linkedin",
      href:
        `http://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${text}`,
      icon: require("@gromia/components/svg/linkedin.svg").default,
      popupHeight: O.some(500),
    }),
    O.some({
      key: "twitter",
      name: "Twitter",
      href: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      icon: require("@gromia/components/svg/twitter.svg").default,
      popupHeight: O.some(250),
    }),
    pipe(
      media,
      O.map(m => ({
        key: "pinterest",
        name: "Pinterest",
        href:
          `https://pinterest.com/pin/create/button/?description=${text}&url=${url}&media=${m}`,
        icon: require("@gromia/components/svg/pinterest.svg").default,
        popupHeight: O.some(650),
      })),
    ),
  ]);
}

function sharePopup(height: number) {
  return function(event: React.SyntheticEvent<HTMLAnchorElement>) {
    event.stopPropagation();

    const screenw = screen.width;
    const screenh = screen.height;
    const w = Math.min(screenw, 600);
    const h = Math.min(screenh, height);
    const l = (screenw - w) / 2;
    const t = (screenh - h) / 2;

    // console.log(`left=${l},top=${t},width=${w},height=${h},toolbar=0`);

    const href = event.currentTarget.href;
    const ok = window.open(
      href,
      "share",
      `left=${l},top=${t},width=${w},height=${h},toolbar=0`,
    );

    // if popup was blocked by blocker, we don't prevent the default link click
    // behaviour
    if (ok) {
      event.preventDefault();
    }
  };
}
